import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";

export type EventType = {
  id: string; code: string; label: string; description: string;
  default_duration_minutes: number; supports_all_day: boolean;
  color: string; order: number; system_default: boolean;
};

type Scope = {
  contact_id?: string; organization_id?: string; opportunity_id?: string;
};

type StaffOption = { id: string; name: string; role?: string };
type RecordOption = {
  id: string; label: string; searchText?: string; contact_id?: string | null; organization_id?: string | null;
};

function responseItems(response: any): any[] {
  const data = response?.data;
  if (Array.isArray(data?.items)) return data.items;
  return Array.isArray(data) ? data : [];
}

type Props = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSuccess: (created: any) => void;
  scope?: Scope;
  currentUserId: string;
  staffOptions?: StaffOption[];  // If not provided, defaults to the current user only.
  defaultStart?: string;         // ISO local "YYYY-MM-DDTHH:MM" preset
};

function makeIdempotencyKey(): string {
  const g: any = globalThis;
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return "ev-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function pad(n: number) { return String(n).padStart(2, "0"); }
function nextHourLocal(): string {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
    "T" + pad(d.getHours()) + ":" + pad(d.getMinutes());
}
function addMinutesLocal(local: string, minutes: number): string {
  const [datePart, timePart] = local.split("T");
  const [y, m, day] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);
  const d = new Date(y, m - 1, day, hh, mm);
  d.setMinutes(d.getMinutes() + minutes);
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) +
    "T" + pad(d.getHours()) + ":" + pad(d.getMinutes());
}
function todayDateStr(): string {
  const d = new Date();
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

type ConflictItem = {
  id: string; title: string;
  start_at?: string | null; end_at?: string | null;
  start_date?: string | null; end_date?: string | null;
  all_day: boolean; status: string;
};

export const EventForm: React.FC<Props> = ({
  open, onOpenChange, onSuccess, scope, currentUserId, staffOptions, defaultStart,
}) => {
  const { user } = useAuth();
  const [types, setTypes] = useState<EventType[]>([]);
  const [loadedStaffOptions, setLoadedStaffOptions] = useState<StaffOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState<boolean>(false);
  const [tenantTz, setTenantTz] = useState<string>("");
  const [requiredVisitFields, setRequiredVisitFields] = useState<string[]>([]);
  const [title, setTitle] = useState<string>("");
  const [typeCode, setTypeCode] = useState<string>("");
  const [allDay, setAllDay] = useState<boolean>(false);
  const [startAt, setStartAt] = useState<string>(defaultStart || nextHourLocal());
  const [endAt, setEndAt] = useState<string>(addMinutesLocal(defaultStart || nextHourLocal(), 60));
  const [startDate, setStartDate] = useState<string>(todayDateStr());
  const [endDate, setEndDate] = useState<string>(todayDateStr());
  const [location, setLocation] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [assignedTo, setAssignedTo] = useState<string>(currentUserId);
  const [contacts, setContacts] = useState<RecordOption[]>([]);
  const [organizations, setOrganizations] = useState<RecordOption[]>([]);
  const [contactQuery, setContactQuery] = useState("");
  const [organizationQuery, setOrganizationQuery] = useState("");
  const [opportunities, setOpportunities] = useState<RecordOption[]>([]);
  const [validOrganizationIds, setValidOrganizationIds] = useState<string[] | null>(null);
  const [contactId, setContactId] = useState<string>(scope?.contact_id || "");
  const [organizationId, setOrganizationId] = useState<string>(scope?.organization_id || "");
  const [opportunityId, setOpportunityId] = useState<string>(scope?.opportunity_id || "");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [conflicts, setConflicts] = useState<ConflictItem[] | null>(null);
  const idemRef = useRef<string>(makeIdempotencyKey());
  const lastAttemptRef = useRef<string>("");
  const scopeKey = JSON.stringify(scope || {}) + "|" + (user?.id ?? "");

  useEffect(() => { idemRef.current = makeIdempotencyKey(); }, [scopeKey, open]);

  useEffect(() => {
    if (!open) return;
    setError(""); setConflicts(null);
    setLoadingTypes(true);
    setAssignedTo(currentUserId);
    const canAssignTeam = ["owner_admin", "admin_support", "manager_trainer"].includes(user?.tenant_role || "");
    Promise.all([
      api.get("/tenant/event-types"),
      api.get("/tenant/settings").catch(() => ({ data: { default_timezone: "UTC" } })),
      api.get("/crm/contacts", { params: { archived: false, limit: 500 } }).catch(() => ({ data: { items: [] } })),
      api.get("/crm/organizations", { params: { archived: false } }).catch(() => ({ data: { items: [] } })),
      api.get("/crm/opportunities").catch(() => ({ data: { items: [] } })),
      api.get("/tenant/workflow-requirements").catch(() => ({ data: { required_fields: {} } })),
      canAssignTeam && !staffOptions
        ? Promise.all([
            api.get("/tenant/users"),
            api.get("/tenant/memberships"),
          ]).catch(() => [null, null])
        : Promise.resolve([null, null]),
    ])
      .then(([tr, sr, cr, or, opr, requirements, staffResult]) => {
        setTypes(tr.data.items || []);
        setTenantTz(sr.data?.default_timezone || "UTC");
        setRequiredVisitFields(requirements.data?.required_fields?.visit || []);
        setContacts(responseItems(cr).map((c: any) => ({
          id: c.id,
          label: `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || c.id,
          searchText: [c.first_name, c.last_name, c.email, c.phone, c.location, c.professional_title, ...(c.specializations || [])].filter(Boolean).join(" ").toLowerCase(),
        })).sort((a: RecordOption, b: RecordOption) => a.label.localeCompare(b.label)));
        setOrganizations(responseItems(or).map((o: any) => ({
          id: o.id,
          label: o.name || o.id,
          searchText: [o.name, o.org_type, o.email, o.phone, o.additional_phone, o.fax, o.website, o.address, o.postal_code].filter(Boolean).join(" ").toLowerCase(),
        })).sort((a: RecordOption, b: RecordOption) => a.label.localeCompare(b.label)));
        setOpportunities(responseItems(opr).map((o: any) => ({
          id: o.id, label: o.name || o.id,
          contact_id: o.contact_id || null, organization_id: o.organization_id || null,
        })));
        if (canAssignTeam && !staffOptions && staffResult?.[0]?.data && staffResult?.[1]?.data) {
          const activeRoles = new Map(
            staffResult[1].data.filter((m: any) => m.active).map((m: any) => [m.user_id, m.tenant_role]),
          );
          setLoadedStaffOptions(staffResult[0].data
            .filter((staff: any) => staff.active && activeRoles.has(staff.id))
            .map((staff: any) => ({ id: staff.id, name: staff.name || staff.email, role: activeRoles.get(staff.id) })));
        } else {
          setLoadedStaffOptions([]);
        }
        setContactId(scope?.contact_id || "");
        setOrganizationId(scope?.organization_id || "");
        setOpportunityId(scope?.opportunity_id || "");
        const first = (tr.data.items || [])[0];
        setTypeCode(first?.code || "");
        if (first) {
          setEndAt(addMinutesLocal(startAt, first.default_duration_minutes || 60));
        }
      })
      .finally(() => setLoadingTypes(false));
  }, [open, currentUserId]);

  const effectiveStaffOptions = staffOptions || loadedStaffOptions;
  const selectedType = useMemo(
    () => types.find((t) => t.code === typeCode) || null,
    [types, typeCode],
  );
  const isVisit = selectedType?.code === "home_visit" || selectedType?.code?.includes("visit");
  const filteredContacts = useMemo(() => {
    const query = contactQuery.trim().toLowerCase();
    return query ? contacts.filter((c) => (c.searchText || c.label.toLowerCase()).includes(query)) : contacts;
  }, [contacts, contactQuery]);
  const filteredOrganizations = useMemo(() => {
    const accessible = validOrganizationIds === null
      ? organizations
      : organizations.filter((o) => validOrganizationIds.includes(o.id));
    const query = organizationQuery.trim().toLowerCase();
    return query ? accessible.filter((o) => (o.searchText || o.label.toLowerCase()).includes(query)) : accessible;
  }, [organizations, validOrganizationIds, organizationQuery]);
  const filteredOpportunities = useMemo(
    () => opportunities.filter((o) =>
      (!contactId || !o.contact_id || o.contact_id === contactId)
      && (!organizationId || !o.organization_id || o.organization_id === organizationId)),
    [opportunities, contactId, organizationId],
  );

  async function changeContact(nextId: string) {
    setContactId(nextId);
    setError("");
    if (!nextId) {
      setValidOrganizationIds(null);
      setOrganizationId("");
      setOpportunityId("");
      return;
    }
    try {
      const { data } = await api.get(`/crm/contacts/${nextId}`);
      const linked = Array.isArray(data?.linked_organizations)
        ? data.linked_organizations.filter((r: any) => r.active !== false && !r.organization_archived)
        : [];
      const ids = linked.map((r: any) => r.organization_id).filter(Boolean);
      setValidOrganizationIds(ids);
      const primary = linked.find((r: any) => r.is_primary)?.organization_id;
      const nextOrganization = primary || (ids.length === 1 ? ids[0] : "");
      setOrganizationId(nextOrganization || "");
      const linkedOppIds = new Set(
        (Array.isArray(data?.linked_opportunities) ? data.linked_opportunities : [])
          .map((o: any) => o.id),
      );
      if (opportunityId && !linkedOppIds.has(opportunityId)) setOpportunityId("");
    } catch {
      setValidOrganizationIds([]);
      setOrganizationId("");
      setOpportunityId("");
      setError("Could not load this contact’s clinic relationships. Try again.");
    }
  }

  // Auto-adjust end when type or start changes (only if end < start).
  useEffect(() => {
    if (!allDay && selectedType) {
      const proposed = addMinutesLocal(startAt, selectedType.default_duration_minutes || 60);
      // Only adjust if user hasn't manually set the end after start.
      if (endAt <= startAt) setEndAt(proposed);
    }
  }, [selectedType, startAt, allDay, endAt]);

  const visitLinkRequired =
    requiredVisitFields.includes("contact_id")
    || requiredVisitFields.includes("organization_id");
  const missingVisitRequirement = isVisit && (
    (visitLinkRequired && !contactId && !organizationId)
    || (requiredVisitFields.includes("opportunity_id") && !opportunityId)
    || (requiredVisitFields.includes("location") && !location.trim())
    || (requiredVisitFields.includes("notes") && !notes.trim())
  );
  const canSubmit =
    !submitting && title.trim().length > 0 && !!typeCode && !!assignedTo
    && (!isVisit || !!contactId || !!organizationId)
    && !missingVisitRequirement;

  async function submit(overrideConflicts: boolean) {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    // Rotate idempotency key when explicitly overriding — override must
    // be a fresh deliberate action, not a replay.
    if (overrideConflicts) idemRef.current = makeIdempotencyKey();

    const payload: any = {
      title: title.trim(),
      event_type_code: typeCode,
      all_day: allDay,
      assigned_to_user_id: assignedTo,
      location: location.trim() || null,
      notes: notes || null,
      override_conflicts: overrideConflicts,
    };
    if (allDay) {
      payload.start_date = startDate;
      payload.end_date = endDate || startDate;
    } else {
      payload.start_timezone = tenantTz || "UTC";
      payload.start_at_local = startAt;
      payload.end_at_local = endAt;
    }
    if (contactId) payload.contact_id = contactId;
    if (organizationId) payload.organization_id = organizationId;
    if (opportunityId) payload.opportunity_id = opportunityId;
    const fingerprint = JSON.stringify(payload);
    if (lastAttemptRef.current && lastAttemptRef.current !== fingerprint) {
      idemRef.current = makeIdempotencyKey();
    }
    lastAttemptRef.current = fingerprint;
    try {
      const r = await api.post("/crm/events", payload, {
        headers: { "Idempotency-Key": idemRef.current },
      });
      idemRef.current = makeIdempotencyKey();
      lastAttemptRef.current = "";
      onSuccess(r.data);
      onOpenChange(false);
      // Reset for next open.
      setTitle(""); setNotes(""); setLocation(""); setConflicts(null);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409
          && detail && typeof detail === "object"
          && detail.code === "event_overlap_conflict") {
        setConflicts(detail.conflicts as ConflictItem[]);
      } else {
        const friendly = detail === "linked_records_inconsistent"
          ? "The selected contact, clinic, and opportunity are not linked. Choose records that belong together."
          : detail === "contact_organization_not_linked"
            ? "This clinic is not linked to the selected contact. Choose one of the contact’s clinics or update their contact record."
          : detail === "idempotency_key_mismatch"
            ? "The appointment changed after the last attempt. Please submit it again."
            : formatApiError(detail);
        if (detail === "idempotency_key_mismatch") idemRef.current = makeIdempotencyKey();
        setError(friendly || "Could not create event");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="event-form-dialog">
        <DialogHeader>
          <DialogTitle>Schedule appointment</DialogTitle>
          <DialogDescription>
            Times are interpreted in the tenant business timezone
            {tenantTz ? <> (<span data-testid="event-form-tenant-tz">{tenantTz}</span>)</> : null}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="event-title">Title <span aria-hidden="true" className="text-rose-600">*</span></Label>
            <Input id="event-title" data-testid="event-title-input"
              value={title} onChange={(e) => setTitle(e.target.value)}
              maxLength={160} placeholder="What is this appointment about?"
              required />
          </div>
          <div>
            <Label htmlFor="event-type">Type <span aria-hidden="true" className="text-rose-600">*</span></Label>
            <Select value={typeCode} onValueChange={setTypeCode}>
              <SelectTrigger data-testid="event-type-select" id="event-type">
                <SelectValue placeholder={loadingTypes ? "Loading…" : "Select type"} />
              </SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.code} value={t.code}
                    data-testid={`event-type-option-${t.code}`}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <Switch id="event-all-day" data-testid="event-all-day-switch"
              checked={allDay}
              disabled={selectedType ? !selectedType.supports_all_day : false}
              onCheckedChange={(v) => setAllDay(!!v)} />
            <Label htmlFor="event-all-day">All-day</Label>
          </div>
          {allDay ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="event-start-date">Start date <span aria-hidden="true" className="text-rose-600">*</span></Label>
                <Input id="event-start-date" data-testid="event-start-date-input"
                  type="date" value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(e.target.value); }} required />
              </div>
              <div>
                <Label htmlFor="event-end-date">End date <span aria-hidden="true" className="text-rose-600">*</span></Label>
                <Input id="event-end-date" data-testid="event-end-date-input"
                  type="date" value={endDate}
                  onChange={(e) => setEndDate(e.target.value)} required />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="event-start-at">Start <span aria-hidden="true" className="text-rose-600">*</span></Label>
                <Input id="event-start-at" data-testid="event-start-at-input"
                  type="datetime-local" value={startAt}
                  onChange={(e) => setStartAt(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="event-end-at">End <span aria-hidden="true" className="text-rose-600">*</span></Label>
                <Input id="event-end-at" data-testid="event-end-at-input"
                  type="datetime-local" value={endAt}
                  onChange={(e) => setEndAt(e.target.value)} required />
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="event-assignee">Assigned to <span aria-hidden="true" className="text-rose-600">*</span></Label>
            {effectiveStaffOptions.length > 1 ? (
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger id="event-assignee" data-testid="event-assignee-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {effectiveStaffOptions.map((s) => (
                    <SelectItem key={s.id} value={s.id}
                      data-testid={`event-assignee-option-${s.id}`}>
                      {s.name || s.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input id="event-assignee" value={user?.name || "You"} readOnly
                data-testid="event-assignee-readonly-input" />
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-md border border-slate-200 p-3">
            <div className="md:col-span-2">
              <div className="text-sm font-medium text-slate-900">Related CRM records</div>
              <div className="text-xs text-slate-500">
                Link the appointment so the team can open the correct doctor, clinic, or opportunity.
                {isVisit ? " A contact or clinic is required for visits." : ""}{isVisit && requiredVisitFields.includes("opportunity_id") ? " An opportunity is required by your workflow." : ""}
              </div>
            </div>
            <div>
              <Label htmlFor="event-contact-search">Contact / doctor</Label>
              <Input id="event-contact-search" value={contactQuery} onChange={(e) => setContactQuery(e.target.value)} placeholder="Search name, phone, email, location…" data-testid="event-contact-search" autoComplete="off" aria-controls="event-contact-search-results" />
              {contactQuery.trim() ? (
                <div id="event-contact-search-results" role="listbox" aria-label="Matching contacts or doctors" className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm" data-testid="event-contact-search-results">
                  {filteredContacts.length ? filteredContacts.slice(0, 10).map((c) => (
                    <button key={c.id} type="button" role="option" aria-selected={contactId === c.id}
                      className="block w-full border-b border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none last:border-b-0"
                      onClick={() => { void changeContact(c.id); setContactQuery(""); }}
                      data-testid={`event-contact-search-result-${c.id}`}>
                      {c.label}
                    </button>
                  )) : (
                    <div className="px-3 py-2 text-sm text-slate-500" data-testid="event-contact-search-empty">No matching contact or doctor.</div>
                  )}
                </div>
              ) : null}
              <div className="mt-1 text-xs text-slate-500">Select a search result above, or browse the full list below.</div>
              <Select value={contactId || "none"} onValueChange={(v) => changeContact(v === "none" ? "" : v)}>
                <SelectTrigger id="event-contact" data-testid="event-contact-select"><SelectValue placeholder="No contact" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No contact</SelectItem>
                  {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="event-organization-search">Organization / clinic</Label>
              <Input id="event-organization-search" value={organizationQuery} onChange={(e) => setOrganizationQuery(e.target.value)} placeholder="Search name, postal code, phone, website…" data-testid="event-organization-search" autoComplete="off" aria-controls="event-organization-search-results" />
              {organizationQuery.trim() ? (
                <div id="event-organization-search-results" role="listbox" aria-label="Matching organizations or clinics" className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm" data-testid="event-organization-search-results">
                  {filteredOrganizations.length ? filteredOrganizations.slice(0, 10).map((o) => (
                    <button key={o.id} type="button" role="option" aria-selected={organizationId === o.id}
                      className="block w-full border-b border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none last:border-b-0"
                      onClick={() => { setOrganizationId(o.id); setOrganizationQuery(""); setError(""); }}
                      data-testid={`event-organization-search-result-${o.id}`}>
                      {o.label}
                    </button>
                  )) : (
                    <div className="px-3 py-2 text-sm text-slate-500" data-testid="event-organization-search-empty">No matching organization or clinic.</div>
                  )}
                </div>
              ) : null}
              <div className="mt-1 text-xs text-slate-500">Select a search result above, or browse the full list below.</div>
              <Select value={organizationId || "none"} onValueChange={(v) => setOrganizationId(v === "none" ? "" : v)}>
                <SelectTrigger id="event-organization" data-testid="event-organization-select"><SelectValue placeholder="No clinic" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No clinic</SelectItem>
                  {filteredOrganizations.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="event-opportunity">Opportunity{isVisit && requiredVisitFields.includes("opportunity_id") ? " *" : " (optional)"}</Label>
              <Select value={opportunityId || "none"} onValueChange={(v) => setOpportunityId(v === "none" ? "" : v)}>
                <SelectTrigger id="event-opportunity" data-testid="event-opportunity-select"><SelectValue placeholder="No opportunity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No opportunity</SelectItem>
                  {filteredOpportunities.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isVisit && !contactId && !organizationId ? (
              <div className="md:col-span-2 text-xs text-amber-700" data-testid="event-visit-link-required">
                Select a contact or clinic before scheduling this visit.
              </div>
            ) : null}
          </div>
          <div>
            <Label htmlFor="event-location">Location{isVisit && requiredVisitFields.includes("location") ? " *" : ""}</Label>
            <Input id="event-location" data-testid="event-location-input"
              value={location} onChange={(e) => setLocation(e.target.value)}
              maxLength={200} placeholder="Optional (address, room, link)" />
          </div>
          <div>
            <Label htmlFor="event-notes">Notes{isVisit && requiredVisitFields.includes("notes") ? " *" : ""}</Label>
            <Textarea id="event-notes" data-testid="event-notes-input"
              value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3} maxLength={4000} />
          </div>
          {error ? (
            <div data-testid="event-form-error" role="alert"
              className="text-sm text-red-600">{error}</div>
          ) : null}
          {conflicts ? (
            <div className="border border-amber-300 bg-amber-50 rounded-md p-3"
                 data-testid="event-form-conflicts">
              <div className="font-medium text-amber-900 text-sm">
                Schedule conflict with {conflicts.length} existing appointment{conflicts.length === 1 ? "" : "s"}.
              </div>
              <ul className="mt-2 space-y-1 text-xs text-amber-900">
                {conflicts.map((c) => (
                  <li key={c.id} data-testid={`event-form-conflict-${c.id}`}>
                    • {c.title} · {c.all_day ? `${c.start_date} → ${c.end_date}` : `${c.start_at} → ${c.end_at}`}
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-xs text-amber-900">
                You can override this conflict and book anyway.
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            data-testid="event-form-cancel-btn">Cancel</Button>
          {conflicts ? (
            <Button onClick={() => submit(true)} disabled={!canSubmit}
              data-testid="event-form-override-btn">
              {submitting ? "Booking…" : "Book anyway (override)"}
            </Button>
          ) : (
            <Button onClick={() => submit(false)} disabled={!canSubmit}
              data-testid="event-form-submit-btn">
              {submitting ? "Saving…" : "Schedule"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
